import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { ProjectDetails } from '@/features/projects/shared/api/get-project-details';
import { useProjectDetails } from '@/features/projects/shared/hooks/use-project-details';
import DateIcon from '@/shared/assets/calendar.svg?react';
import { Link } from '@/shared/components/ui/link';
import { cn } from '@/shared/utils/cn';
import { formatDate } from '@/shared/utils/format';

type DefaultLayoutProps = {
  title?: string;
  titleBig?: boolean;
  headerActions?: React.ReactNode[];
  children: React.ReactNode;
  headerActionsPosition?: 'top' | 'bottom';
  navigationActionRowClassName?: string;
};

export const DefaultLayout = ({
  children,
  title,
  titleBig = false,
  headerActions = [],
  headerActionsPosition = 'bottom',
  navigationActionRowClassName,
}: DefaultLayoutProps) => {
  return (
    <div className="bg-dark-blue">
      <NavigationActionRow
        actions={headerActionsPosition === 'top' ? headerActions : []}
        navigationActionRowClassName={navigationActionRowClassName}
      />
      <LayoutHeader
        title={title}
        titleBig={titleBig}
        actions={headerActionsPosition === 'bottom' ? headerActions : []}
      />
      {children}
    </div>
  );
};

const NavigationActionRow = ({
  actions,
  navigationActionRowClassName,
}: {
  actions?: React.ReactNode[];
  navigationActionRowClassName?: string;
}) => {
  const location = useLocation();
  const { projectId } = useParams();
  const { t } = useTranslation('common');

  const navigationItems = getNavigationItems(location.pathname, t, projectId);

  if (navigationItems.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'mb-11 mt-9 flex flex-row border-b border-white/50 pb-3',
        navigationActionRowClassName,
      )}
    >
      {navigationItems.map((item, index) => (
        <PathNavigationItem
          key={item.path}
          title={item.title}
          path={item.path}
          isActive={location.pathname === item.path}
          isLast={index === navigationItems.length - 1}
          projectId={item.projectId}
        />
      ))}
      {actions && <LayoutActions actions={actions} />}
      <div className="ml-4 flex flex-row items-center gap-2 rounded-md bg-light-container px-3 py-2">
        <DateIcon className="size-4" />
        <span className="text-2xs font-medium">
          {formatDate({ date: new Date(), formatType: 'dayWordThenFullDate' })}
        </span>
      </div>
    </div>
  );
};

const NavigationLink = ({
  to,
  children,
}: {
  to: string;
  children: React.ReactNode;
}) => (
  <Link to={to} className="text-2xs text-custom-light-purple">
    {children}
  </Link>
);

const NavigationSeparator = () => (
  <span className="mx-2 text-2xs text-custom-light-purple">/</span>
);

const PathNavigationItem = ({
  title,
  path,
  isLast,
  projectId,
}: {
  title: string;
  path: string;
  isActive: boolean;
  isLast: boolean;
  projectId?: string;
}) => {
  var project: ProjectDetails | undefined;
  const pathEndsWithProjectId = path.endsWith(projectId ?? '');
  if (projectId && pathEndsWithProjectId) {
    const { data } = useProjectDetails({
      projectId: projectId,
    });
    project = data;
  }

  const displayTitle = project?.name || title;

  return (
    <div className="flex items-center">
      {project?.parent && (
        <>
          <NavigationLink
            to={path.replace(`/${projectId}`, `/${project.parent.id}`)}
          >
            {project.parent.name}
          </NavigationLink>
          <NavigationSeparator />
        </>
      )}
      <NavigationLink to={path}>{displayTitle}</NavigationLink>
      {!isLast && <NavigationSeparator />}
    </div>
  );
};

const LayoutHeader = ({
  title,
  titleBig,
  actions,
}: {
  title?: string;
  titleBig: boolean;
  actions?: React.ReactNode[];
}) => {
  return (
    <div className="mb-6 flex flex-row items-center gap-2">
      {title && <LayoutTitle title={title} titleBig={titleBig} />}
      {actions && <LayoutActions actions={actions} />}
    </div>
  );
};

const LayoutTitle = ({
  title,
  titleBig,
}: {
  title: string;
  titleBig: boolean;
}) => {
  return (
    <h1 className={cn('text-base', titleBig && 'text-2xl font-medium')}>
      {title}
    </h1>
  );
};

const LayoutActions = ({ actions }: { actions: React.ReactNode[] }) => {
  return <div className="ml-auto flex flex-row space-x-3">{actions}</div>;
};

const getNavigationItems = (
  currentPath: string,
  t: TFunction<'common', undefined>,
  projectId?: string,
) => {
  const pathSegments = currentPath.split('/').filter(Boolean);
  const navigationItems: Array<{
    title: string;
    path: string;
    projectId?: string;
  }> = [];

  let currentPathBuilder = '';

  for (let i = 0; i < pathSegments.length; i++) {
    const segment = pathSegments[i];

    currentPathBuilder += `/${segment}`;

    const segmentTitle = getSegmentTitle(segment, t);
    if (segmentTitle) {
      const isProjectSegment = segment === projectId;
      navigationItems.push({
        title: segmentTitle,
        path: currentPathBuilder,
        projectId: isProjectSegment ? projectId : undefined,
      });
    }
  }

  return navigationItems;
};

const getSegmentTitle = (
  segment: string,
  t: TFunction<'common', undefined>,
): string | null => {
  const knownSegments: Record<string, string> = {
    projects: t('navbar.projects'),
    documents: t('navbar.documents'),
    partners: t('navbar.partners'),
    notifications: t('navbar.messages'),
    insights: t('navbar.insights'),
    forecast: t('navbar.forecast'),
    'tracking-report-history': t('navbar.trackingReportHistory'),
    'member-management': t('navigation.member_management'),
  };

  if (knownSegments[segment]) {
    return knownSegments[segment];
  }

  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
};
